import { yupResolver } from "@hookform/resolvers/yup";
import { Close } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import {
  AppointmentData,
  getRandomColorCode,
  useAppointments,
} from "hooks/useAppointments";
import { ProviderDto } from "libs/cellera-sdk";
import PatientSearchDropDown from "pages/components/Patient/PatientSearchDropDown";
import ProviderSearchDropDown from "pages/components/Provider/ProviderSearchDropDown";
import React, { useState, useEffect, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { themeColors } from "theme/colorTokens";
import { Patient } from "types";
import {
  AppointmentFormData,
  appointmentSchema,
} from "validations/appointmentSchemas";

interface AddAppointmentModalProps {
  open: boolean;
  onClose: (isRefresh: boolean) => void;
}

const AddAppointmentModal: React.FC<AddAppointmentModalProps> = ({
  open,
  onClose,
}) => {
  const { showError, showSuccess } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createAppointment, error } = useAppointments();
  const { userClaims } = useAuth();
  const { isPracticeOwner = false } = userClaims || {};
  const [provider, setProvider] = useState<ProviderDto | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);

  // Helper function to get next o'clock time
  const getNextOClockTime = () => {
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1, 0, 0, 0);
    return nextHour;
  };

  // Helper function to get time 1 hour later
  const getOneHourLater = (startTime: Date) => {
    const endTime = new Date(startTime);
    endTime.setHours(startTime.getHours() + 1);
    return endTime;
  };

  useEffect(() => {
    if (error) {
      showError("Error saving appointment:", error);
    }
  }, [error, showError]);

  const formData = useForm({
    resolver: yupResolver(appointmentSchema),
    defaultValues: {
      patientId: patient?.patientId || "",
      providerId: provider?.id,
      startDate: new Date(),
      startTime: getNextOClockTime(),
      endTime: getOneHourLater(getNextOClockTime()),
      colorCode: getRandomColorCode(),
      notes: "",
    },
  });

  // Populate form when editing an appointment
  useEffect(() => {
    formData.setValue("patientId", patient?.patientId || "");
    formData.setValue("providerId", provider?.id);
    formData.setValue("startDate", new Date());
    formData.setValue("startTime", getNextOClockTime());
    formData.setValue("endTime", getOneHourLater(getNextOClockTime()));
    formData.setValue("colorCode", getRandomColorCode());
    formData.setValue("notes", "");
    formData.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = useCallback(
    <K extends keyof AppointmentFormData>(
      field: K,
      value: AppointmentFormData[K]
    ) => {
      formData.setValue(field as keyof AppointmentFormData, value);

      // Clear error when user starts typing
      formData.clearErrors(field);

      // If changing start time, auto-update end time for new appointments
      if (field === "startTime" && value) {
        const newEndTime = getOneHourLater(value as Date);
        formData.setValue("endTime", newEndTime);
      }

      // Validate that end time is still after start time
      if (
        field === "startTime" &&
        value &&
        formData.getValues("endTime") &&
        formData.getValues("startDate")
      ) {
        const startDateTime = new Date(formData.getValues("startDate"));
        startDateTime.setHours((value as Date).getHours());
        startDateTime.setMinutes((value as Date).getMinutes());

        const endDateTime = new Date(formData.getValues("startDate"));
        endDateTime.setHours(formData.getValues("endTime").getHours());
        endDateTime.setMinutes(formData.getValues("endTime").getMinutes());

        if (endDateTime <= startDateTime) {
          formData.setError("endTime", {
            message: "End time must be after start time",
          });
        }
      }
    },
    [formData]
  );

  const handleProviderChange = useCallback(
    (provider: ProviderDto | null) => {
      setProvider(provider);
      if (provider) {
        handleInputChange("providerId", provider.id);
      } else {
        handleInputChange("providerId", "");
      }
    },
    [handleInputChange]
  );

  const handlePatientChange = useCallback(
    (patient: Patient | null) => {
      setPatient(patient);
      if (patient) {
        handleInputChange("patientId", patient.patientId);
      } else {
        handleInputChange("patientId", "");
      }
    },
    [handleInputChange]
  );

  const handleClose = useCallback(
    (isRefresh: boolean = false) => {
      const nextOClock = getNextOClockTime();
      formData.reset({
        patientId: "",
        providerId: "",
        startDate: new Date(),
        startTime: nextOClock,
        endTime: getOneHourLater(nextOClock),
        colorCode: getRandomColorCode(),
        notes: "",
      });
      formData.clearErrors();
      setPatient(null);
      setProvider(null);
      onClose(isRefresh);
    },
    [formData, onClose]
  );

  const onSubmit = useCallback(
    async (data: AppointmentFormData) => {
      setIsSubmitting(true);
      const appointmentData: AppointmentData = {
        patientId: data.patientId,
        providerId: data.providerId,
        startDate: data.startDate,
        startTime: data.startTime,
        endTime: data.endTime,
        colorCode: data.colorCode,
        notes: data.notes,
      };
      await createAppointment(appointmentData);
      showSuccess(
        "Appointment Created",
        "Appointment has been successfully created."
      );
      setIsSubmitting(false);
      handleClose(true);
    },
    [createAppointment, handleClose, showSuccess]
  );

  const handleStartTimeChange = useCallback(
    (time: Date | null) => {
      if (time) {
        handleInputChange("startTime", time);
      }
    },
    [handleInputChange]
  );

  const handleEndTimeChange = useCallback(
    (time: Date | null) => {
      if (time) {
        handleInputChange("endTime", time);
      }
    },
    [handleInputChange]
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (date) {
        handleInputChange("startDate", date);
      }
    },
    [handleInputChange]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
            },
          },
        }}
      >
        <FormProvider {...formData}>
          <form onSubmit={formData.handleSubmit(onSubmit)}>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "18px 24px",
                borderBottom: "none",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: themeColors.text.primary,
                  fontSize: "24px",
                }}
              >
                Add Appointment
              </Typography>
              <IconButton
                onClick={() => handleClose(false)}
                size="small"
                sx={{
                  color: themeColors.text.primary,
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent
              sx={{
                padding: "24px",
                paddingTop: "16px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <PatientSearchDropDown
                  onChange={handlePatientChange}
                  error={Boolean(formData.formState.errors.patientId)}
                  helperText={formData.formState.errors.patientId?.message}
                />
                {isPracticeOwner && (
                  <ProviderSearchDropDown
                    onChange={handleProviderChange}
                    error={Boolean(formData.formState.errors.providerId)}
                    helperText={formData.formState.errors.providerId?.message}
                  />
                )}

                <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
                  <DatePicker
                    label="Date"
                    value={formData.getValues("startDate")}
                    onChange={handleDateChange}
                    format="EEEE, MMMM d"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(formData.formState.errors.startDate),
                        helperText:
                          formData.formState.errors.startDate?.message,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                          },
                        },
                      },
                    }}
                  />

                  <Box sx={{ flex: 1 }}>
                    <TimePicker
                      label="Start Time"
                      value={formData.getValues("startTime")}
                      onChange={handleStartTimeChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(formData.formState.errors.startTime),
                          helperText:
                            formData.formState.errors.startTime?.message,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TimePicker
                      label="End Time"
                      value={formData.getValues("endTime")}
                      onChange={handleEndTimeChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(formData.formState.errors.endTime),
                          helperText:
                            formData.formState.errors.endTime?.message,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <FormControl fullWidth>
                    <TextField
                      label="Note"
                      name="notes"
                      multiline
                      rows={4}
                      value={formData.watch("notes")}
                      onChange={(e) =>
                        formData.setValue("notes", e.target.value)
                      }
                      fullWidth
                      slotProps={{
                        input: {
                          name: "notes",
                          inputProps: {
                            maxLength: 2000,
                          },
                        },
                        formHelperText: {
                          sx: {
                            marginLeft: 0,
                            marginRight: 0,
                            display: "flex",
                            justifyContent: "space-between",
                          },
                        },
                      }}
                      error={Boolean(formData.formState.errors.notes)}
                      helperText={
                        formData.formState.errors.notes?.message ||
                        `${formData.getValues("notes")?.length || 0}/2000`
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </FormControl>
                </Box>
              </Box>
            </DialogContent>
            <Divider />

            <DialogActions
              sx={{
                padding: "18px 24px",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={() => handleClose(false)}
                variant="outlined"
                sx={{
                  borderColor: themeColors.border.main,
                  color: themeColors.text.primary,
                  padding: "12px 32px",
                  borderRadius: "24px",
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  minWidth: "140px",
                  "&:hover": {
                    borderColor: themeColors.primary.main,
                    backgroundColor: themeColors.primary.light + "10",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: themeColors.primary.main,
                  color: themeColors.text.inverse,
                  padding: "12px 32px",
                  borderRadius: "24px",
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  minWidth: "140px",
                  "&:hover": {
                    backgroundColor: themeColors.primary.dark,
                  },
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} /> : "Save"}
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddAppointmentModal;
